<template>
  <div id="page-recu" class="d-print-block">

    <!-- ✅ Boutons hors impression -->
    <div class="text-end my-3 d-print-none">
      <button class="btn btn-outline-primary me-2" @click="imprimer">
        <i class="bi bi-printer me-1"></i> Imprimer
      </button>
      <button class="btn btn-outline-success" @click="telechargerPDF">
        <i class="bi bi-download me-1"></i> Télécharger
      </button>
    </div>

    <!-- ✅ Zone imprimable A4 -->
    <div id="recu-print" ref="recu">

      <!-- HEADER (réduit, aucune débordement) -->
      <header>
        <div class="header-left">
          <img
            v-if="systeme?.logoUrl"
            :src="systeme.logoUrl"
            alt="Logo Clinique"
            class="logo"
          />
          <div class="info">
            <h2>{{ systeme?.nom || "Clinique Médicale" }}</h2>
            <p>{{ systeme?.adresse || "" }}</p>
            <p>Tél : {{ systeme?.telephone || "" }}</p>
          </div>
        </div>

        <div class="header-right">
          <p><strong>Réf :</strong> {{ reference }}</p>
          <p><strong>Date :</strong> {{ formatDate(hospitalisation.dateEntree) }}</p>
        </div>
      </header>

      <!-- ✅ CONTENU -->
      <div class="content">

        <h3>REÇU D’HOSPITALISATION</h3>

        <!-- Infos Patient -->
        <section class="section-block">
          <h4>Informations du patient</h4>
          <p><strong>Nom :</strong> {{ hospitalisation.patient?.nom }} {{ hospitalisation.patient?.prenom }}</p>
          <p><strong>Chambre :</strong> {{ hospitalisation.chambre?.numero }}  
             &nbsp;&nbsp; <strong>Lit :</strong> {{ hospitalisation.lit?.numero }}</p>
          <p>
            <strong>Montant :</strong> {{ hospitalisation.montantFacture?.toLocaleString() }} FCFA
            &nbsp;&nbsp; <strong>Statut :</strong> {{ hospitalisation.estPaye }}
          </p>
        </section>

        <!-- Détails hospitalisation -->
        <section class="section-block">
          <h4>Détails de l’hospitalisation</h4>

          <table>
            <thead>
              <tr>
                <th>Information</th>
                <th>Détails</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Date d'entrée</td>
                <td>{{ formatDate(hospitalisation.dateEntree) }}</td>
              </tr>
              <tr>
                <td>Date sortie prévue</td>
                <td>{{ formatDate(hospitalisation.dateSortiePrevue) }}</td>
              </tr>
              <tr>
                <td>Date sortie réelle</td>
                <td>{{ formatDate(hospitalisation.dateSortieReelle) }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- QR CODE -->
        <div class="qr-container">
          <qrcode-vue :value="hospitalisation.id?.toString()" :size="110" />
          <p class="text-muted mt-2">ID : {{ hospitalisation.id }}</p>
        </div>

      </div>

      <!-- FOOTER -->
      <footer>
        <div>Bamako, le {{ new Date().toLocaleDateString() }}</div>
        <div class="signature">Signature & Cachet</div>
      </footer>

    </div>
  </div>
</template>

<script>
import api from "@/api";
import dayjs from "dayjs";
import QRCodeVue from "qrcode.vue";
import html2pdf from "html2pdf.js";

export default {
  components: { "qrcode-vue": QRCodeVue },
  data() {
    return {
      hospitalisation: {},
      reference: "",
      systeme: null,
    };
  },
  created() {
    this.getHospitalisation();
    this.getConfigurationSysteme();
  },
  methods: {
    formatDate(date) {
      return date ? dayjs(date).format("DD-MM-YYYY HH:mm") : "—";
    },
    async getHospitalisation() {
      const { id } = this.$route.params;
      const res = await api.getItem("hospitalisations", id);
      this.hospitalisation = res.data;
      this.reference = `HOS-${dayjs().format("YYYYMMDD")}-${id.toString().padStart(5, "0")}`;
    },
    async getConfigurationSysteme() {
      const res = await api.getItems("configurations");
      this.systeme = res.data.data[0];
      const base = process.env.VUE_APP_API_BASE_URL || "";
      if (this.systeme.logo) this.systeme.logoUrl = base + this.systeme.logo;
    },
    imprimer() {
      window.print();
    },
    telechargerPDF() {
      html2pdf()
        .from(this.$refs.recu)
        .set({
          margin: 0,
          filename: this.reference + ".pdf",
          html2canvas: { scale: 2 },
          jsPDF: { format: "a4", unit: "pt" },
        })
        .save();
    },
  },
};
</script>

<style scoped>

/* ✅ PAGE A4 structure */
@page {
  margin: 2cm;
}

body {
  font-family: "Times New Roman", serif;
  font-size: 14px;
}

/* ✅ HEADER optimisé (110px → plus aucun débordement) */
header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 110px;
  padding: 5px 2cm;
  border-bottom: 2px solid #0056b3;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  width: 70px;
  height: 70px;
  object-fit: contain;
}

.info h2 {
  margin: 0;
  font-size: 18px;
  color: #0056b3;
}

.info p {
  margin: 1px 0;
}

/* ✅ FOOTER réduit (65px) */
footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 65px;
  padding: 5px 2cm;
  border-top: 2px solid #0056b3;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ✅ CONTENU qui ne dépasse plus */
.content {
  margin-top: 130px;
  margin-bottom: 90px;
  padding: 0 2cm;
}

/* TITRE principal */
h3 {
  text-align: center;
  color: #0056b3;
  text-decoration: underline;
}

/* ✅ SECTIONS */
.section-block {
  margin-top: 18px;
}

.section-block h4 {
  margin-bottom: 6px;
  color: #0056b3;
  font-size: 15px;
}

/* ✅ Table adaptée A4 */
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
}

th, td {
  border: 1px solid #0056b3;
  padding: 6px;
  font-size: 13px;
}

th {
  background: #e6f0ff;
}

/* ✅ QR */
.qr-container {
  text-align: center;
  margin-top: 20px;
}

</style>
